import { warn } from '@execonline-inc/logging';
import { InvalidUrlError, URLParser, putQueryParam, toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { Task } from 'taskarian';
import GroupsStore from '.';
import { AppyError, callApi } from '../Appy';
import { currentUserStore } from '../CurrentUserStore';
import { handleError } from '../ErrorActionableReaction';
import { MissingLinkError, findLinkT } from '../LinkyLinky';
import { SearchParameters } from '../ListFilter/Types';
import ReactionComponent, { RCProps } from '../ReactionComponent';
import { Link } from '../Resource/Types';
import { groupResourcesDecoder } from './Decoders';
import { GroupsResource, LoadingMore, State } from './Types';

interface Props extends RCProps<GroupsStore> {}

type GroupsError = AppyError | InvalidUrlError | MissingLinkError;

const handleGroupsError = (store: GroupsStore) => (error: GroupsError) => {
  switch (error.kind) {
    case 'missing-link-error':
    case 'invalid-url-error':
      store.error('We are unable to process your request at this time');
      warn('GroupsReactions', error.kind, error);
      break;
    default:
      handleError(store, error);
  }
};

const sendRequest = callApi(groupResourcesDecoder, {});

const updateSearchParams = (searchParameters: SearchParameters) => (url: URLParser) =>
  searchParameters.reduce(
    (acc, parameter) => putQueryParam(`user_groups_filter[${parameter.key}]`, parameter.value, acc),
    url,
  );

const buildUrl = (store: GroupsStore, page: number, link: Link): Task<GroupsError, Link> =>
  toUrlT(link.href)
    .map(updateSearchParams(store.searchParameters))
    .map(putQueryParam('page', String(page)))
    .map((url): Link => ({ ...link, href: url.href }));

const getGroups = (store: GroupsStore, page: number) => (link: Link) =>
  buildUrl(store, page, link).andThen(sendRequest);

const concatGroups =
  (state: LoadingMore) =>
  (newResource: GroupsResource): GroupsResource => ({
    ...newResource,
    payload: state.groupsResource.payload.concat(newResource.payload),
  });

class GroupsReactions extends ReactionComponent<GroupsStore, State, Props> {
  tester = () => this.props.store.state;
  effect = (state: State) => {
    const { store } = this.props;
    switch (state.kind) {
      case 'initializing':
        break;
      case 'updating-search-parameters':
      case 'loading':
        Task.succeed<GroupsError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('user-groups'))
          .andThen(getGroups(store, state.page))
          .fork(handleGroupsError(store), store.ready);
        break;
      case 'ready':
      case 'error':
        break;
      case 'updating-query':
        store.ready(state.groupsResource);
        break;
      case 'sorting':
        store.loading(1);
        break;
      case 'loading-more':
        Task.succeed<GroupsError, ReadonlyArray<Link>>(currentUserStore.links)
          .andThen(findLinkT('user-groups'))
          .andThen(getGroups(store, state.page))
          .map(concatGroups(state))
          .fork(handleGroupsError(store), store.ready);
        break;
      default:
        assertNever(state);
    }
  };
}

export default GroupsReactions;
